package com.klantenstop.web.components.search

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.klantenstop.web.components.CategoryRequestHelper
import com.klantenstop.web.components.CustomSelect
import com.klantenstop.web.data.models.domain.Category
import com.klantenstop.web.data.models.domain.Radius
import com.klantenstop.web.data.models.domain.SearchParams
import com.klantenstop.web.data.service.CitySuggestion
import com.klantenstop.web.data.service.NominatimCityService
import com.klantenstop.web.navigation.Navigation
import com.materialkobweb.components.widgets.DsEditableField
import com.materialkobweb.components.widgets.DsFlexBox
import com.materialkobweb.components.widgets.FilledTonalButtonLink
import com.materialkobweb.styles.MaterialColorVars
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.filter
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event

/**
 * Search form component with local state management.
 *
 * This component maintains internal state for form fields (query, category, radius)
 * and is initialized from [initialSearchParams]. When [initialSearchParams] changes (e.g., via URL
 * navigation), all internal state is reset to match the new initial values.
 *
 * @param categories List of categories to display in dropdown (provided by parent)
 * @param initialSearchParams Initial values for the form (from URL on SearchPage, empty on HomePage)
 * @param horizontal Whether to display fields horizontally or vertically
 * @param collapsible When true, shows a [DsEditableField] summary when idle. Tapping it expands the
 *   full form. Clicking anywhere outside the form collapses it back. The form also auto-collapses
 *   whenever [initialSearchParams] changes (i.e. after a new search completes and the URL updates).
 * @param validateFields Whether to show validation errors
 * @param modifier Styling modifier
 */
@OptIn(FlowPreview::class)
@Composable
fun SearchSection(
    categories: List<Category>,
    initialSearchParams: SearchParams = SearchParams(),
    horizontal: Boolean = false,
    collapsible: Boolean = false,
    validateFields: Boolean = false,
    modifier: Modifier = Modifier
) {
    val ctx = rememberPageContext()
    val isSearchPage = ctx.route.path.startsWith("/zoeken")

    // When collapsible, start collapsed and auto-collapse again whenever initialSearchParams changes
    // (i.e. after the user completes a search and the URL updates with new params).
    var isExpanded by remember(initialSearchParams, collapsible) { mutableStateOf(!collapsible) }

    // Local form state - reset when initialSearchParams change
    var query by remember(initialSearchParams) { mutableStateOf(initialSearchParams.query) }
    var selectedCategory by remember(initialSearchParams) { mutableStateOf(initialSearchParams.category) }
    var selectedRadius by remember(initialSearchParams) { mutableStateOf(initialSearchParams.radius) }

    val cityService = NominatimCityService

    // Autocomplete state is purely transient local UI state — no dependency on initialSearchParams.
    var citySuggestions by remember { mutableStateOf<List<CitySuggestion>>(emptyList()) }
    var cityError by remember { mutableStateOf(false) }
    val queryFlow = remember { MutableSharedFlow<String>(extraBufferCapacity = 1) }

    LaunchedEffect(Unit) {
        queryFlow
            .debounce(300)
            .filter { it.length >= 2 }
            .distinctUntilChanged()
            .collectLatest { searchQuery ->
                citySuggestions = cityService.autocomplete(searchQuery)
            }
    }

    val ranges = remember { Radius.entries }

    // Unique container ID used by the outside-click listener to determine bounds.
    val containerId = remember { "collapsible-search-section" }

    // Collapse the form when the user clicks anywhere outside the expanded container.
    DisposableEffect(isExpanded) {
        if (!collapsible || !isExpanded) return@DisposableEffect onDispose {}

        val handler: (Event) -> Unit = handler@{ event ->
            val clicked = event.target as? HTMLElement ?: return@handler
            val container = document.getElementById(containerId) as? HTMLElement ?: return@handler
            if (!container.contains(clicked)) {
                isExpanded = false
            }
        }
        document.addEventListener("click", handler, true)
        onDispose { document.removeEventListener("click", handler, true) }
    }

    if (collapsible && !isExpanded) {
        // Collapsed state: a read-only field summarising the active search. Tapping it expands.
        val summaryText = buildString {
            if (initialSearchParams.query.isNotBlank()) append(initialSearchParams.query)
            initialSearchParams.category?.let {
                if (isNotEmpty()) append(" · ")
                append(it.name)
            }
            if (isNotEmpty()) append(" · ${initialSearchParams.radius.range} km")
        }.ifBlank { "Zoek op categorie, eventueel aangevuld met een gemeente..." }

        DsEditableField(
            id = "search-readonly-field",
            modifier = modifier
                .cursor(Cursor.Pointer)
                .onClick { isExpanded = true },
            label = "Zoeken",
            value = summaryText,
            onValueChange = {},
            readOnly = true,
            required = false,
            backgroundColor = MaterialColorVars.SurfaceContainer.value()
        )
    } else {
        SearchSectionContent(
            // Attach the container ID so the outside-click listener can find the boundary.
            modifier = if (collapsible) {
                modifier.attrsModifier { id(containerId) }
            } else {
                modifier
            },
            isSearchPage = isSearchPage,
            horizontal = horizontal,
            query = query,
            selectedCategory = selectedCategory,
            selectedRadius = selectedRadius,
            categories = categories,
            ranges = ranges,
            citySuggestions = citySuggestions,
            cityError = cityError,
            validateFields = validateFields,
            onQueryChange = { newQuery ->
                query = newQuery

                // Emit to flow - collectLatest will cancel in-flight requests when new value arrives
                queryFlow.tryEmit(newQuery)

                // Clear suggestions immediately if query is too short
                if (newQuery.length < 2) {
                    citySuggestions = emptyList()
                }
            },
            onCategorySelect = { index ->
                selectedCategory = categories.getOrNull(index)
            },
            onRadiusSelect = { index ->
                selectedRadius = ranges.getOrNull(index) ?: Radius.Km5
            },
            onCitySelect = { suggestion ->
                query = suggestion.name
                citySuggestions = emptyList()
                cityError = false
            },
            onCityDismiss = { citySuggestions = emptyList() },
            onSearchClick = {
                cityError = selectedCategory == null
            }
        )
    }
}

@Composable
private fun SearchSectionContent(
    modifier: Modifier = Modifier,
    isSearchPage: Boolean,
    horizontal: Boolean = false,
    query: String,
    selectedCategory: Category?,
    selectedRadius: Radius,
    categories: List<Category>,
    ranges: List<Radius>,
    citySuggestions: List<CitySuggestion>,
    cityError: Boolean,
    validateFields: Boolean,
    onQueryChange: (String) -> Unit,
    onCategorySelect: (Int) -> Unit,
    onRadiusSelect: (Int) -> Unit,
    onCitySelect: (CitySuggestion) -> Unit,
    onCityDismiss: () -> Unit,
    onSearchClick: () -> Unit
) {
    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        DsFlexBox(
            modifier = Modifier.fillMaxWidth(),
            flexDirection = if (!horizontal) FlexDirection.Column else FlexDirection.Row,
            alignItems = if (!horizontal) AlignItems.Start else AlignItems.FlexEnd,
            gap = 20.px
        ) {
            CityAutocomplete(
                modifier = Modifier.fillMaxWidth(),
                id = "city",
                label = "Gemeente",
                placeholder = "Jouw stad of gemeente",
                value = query,
                suggestions = citySuggestions,
                valid = true, // City is always optional -- only the category below is required
                onValueChange = onQueryChange,
                onSuggestionSelect = onCitySelect,
                onDismiss = onCityDismiss
            )

            if (cityError && !horizontal) {
                CityErrorText(Modifier.margin(bottom = 16.px))
            }

            Column(
                modifier = Modifier.fillMaxWidth()
            ) {
                CustomSelect(
                    modifier = Modifier.fillMaxWidth(),
                    id = "dropdown-search-category",
                    label = "Categorie",
                    placeholder = "Selecteer categorie",
                    items = categories.map { it.name },
                    selectedItem = selectedCategory?.name,
                    allowClearSelection = false,
                    valid = !validateFields || selectedCategory != null,
                    onItemSelect = { index, _ -> onCategorySelect(index) }
                )

                if (!horizontal) {
                    CategoryRequestHelper(source = "search_category_dropdown")
                }
            }

            CustomSelect(
                modifier = Modifier.fillMaxWidth(),
                id = "dropdown-search-radius",
                label = "Afstand",
                placeholder = "",
                items = ranges.map { it.range.toString() + " km" },
                selectedItem = selectedRadius.range.toString() + " km",
                onItemSelect = { index, _ -> onRadiusSelect(index) }
            )

            FilledTonalButtonLink(
                modifier = Modifier.minWidth(100.px)
                    .onClick { onSearchClick() },
                path = if (selectedCategory != null) {
                    Navigation.Screen.Search(
                        SearchParams(
                            query = query,
                            category = selectedCategory,
                            radius = selectedRadius
                        )
                    ).route
                } else {
                    rememberPageContext().route.path // Category is required - stay put so the validation error stays visible
                },
                updateHistoryMode = if (isSearchPage && selectedCategory != null) {
                    UpdateHistoryMode.REPLACE
                } else null
            ) {
                Text("Zoeken")
            }
        }

        if (cityError && horizontal) {
            CityErrorText(
                modifier = Modifier
                    .fillMaxWidth(80.percent)
                    .margin(top = 16.px)
            )
        }
    }
}

@Composable
private fun CityErrorText(
    modifier: Modifier = Modifier
) {
    SpanText(
        modifier = modifier
            .textAlign(TextAlign.Center)
            .color(MaterialColorVars.Error.value()),
        text = "Kies een categorie om te kunnen zoeken.",
    )
}
